import { Injectable } from '@angular/core';
import {
    ActivesResponsibilityControllerService,
    PersonalReductionRequisition,
    PersonalReductioRequisitionControllerService,
} from '@api-swagger-client/index';
import { PersonalReductionRequisitionWithRelations } from '@api-swagger-client/model/personalReductionRequisitionWithRelations';
import { Observable, catchError, map, tap, throwError } from 'rxjs';
import { PersonalReductionRequisitionFilter } from '@api-swagger-client/model/personalReductionRequisitionFilter';
export const personalReductionFilter: any | PersonalReductionRequisitionFilter =
    JSON.stringify({
        fields: {
            id: true,
            code: true,
            employeeId: true,
            employeeName: true,
            employeeEnrollment: true,
            employeeCompany: true,
            employeeDepartment: true,
            employeeArea: true,
            transactionEffectiveDate: true,
            movementType: true,
            reason: true,
            other: true,
            token: true,
            companyId: true,
            humanCapitalCompanyId: true,
            statusId: true,
            createdBy: true,
        },
        include: [
            {
                relation: 'company',
                scope: {
                    fields: { id: true, name: true, color: true },
                },
            },
            {
                relation: 'status',
                scope: {
                    fields: { id: true, name: true, code: true },
                },
            },
            {
                relation: 'employeeDepartmentRelation',
                scope: {
                    fields: { id: true, name: true },
                },
            },
            {
                relation: 'humanCapitalCompany',
                scope: {
                    fields: {
                        id: true,
                        humanCapitalManagerId: true,
                        humanCapitalDirectorId: true,
                        payrollManagerId: true,
                    },
                    include: [
                        {
                            relation: 'humanCapitalManager',
                            scope: {
                                fields: {
                                    id: true,
                                    firstName: true,
                                    middleName: true,
                                    lastName: true,
                                    photoProfileUrl: true,
                                },
                            },
                        },
                        {
                            relation: 'humanCapitalDirector',
                            scope: {
                                fields: {
                                    id: true,
                                    firstName: true,
                                    middleName: true,
                                    lastName: true,
                                    photoProfileUrl: true,
                                },
                            },
                        },
                        {
                            relation: 'payrollManager',
                            scope: {
                                fields: {
                                    id: true,
                                    firstName: true,
                                    middleName: true,
                                    lastName: true,
                                    photoProfileUrl: true,
                                },
                            },
                        },
                    ],
                },
            },
            {
                relation: 'activesReductionRequisitions',
                scope: {
                    fields: {
                        id: true,
                        activeResponsibilityId: true,
                        personalReductionId: true,
                    },
                    include: [
                        {
                            relation: 'activeResponsibility',
                            scope: {
                                fields: { id: true, name: true },
                            },
                        },
                    ],
                },
            },
            {
                relation: 'requisitionStatusHistories',
                scope: {
                    fields: {
                        id: true,
                        statusId: true,
                        reductionRequisitionId: true,
                        comments: true,
                        createdOn: true,
                        createdBy: true,
                    },
                    order: ['createdOn DESC'],
                    include: [
                        {
                            relation: 'status',
                            scope: {
                                fields: { id: true, name: true, code: true },
                            },
                        },
                        {
                            relation: 'changedBy',
                            scope: {
                                fields: {
                                    id: true,
                                    firstName: true,
                                    middleName: true,
                                    lastName: true,
                                    photoProfileUrl: true,
                                },
                            },
                        },
                    ],
                },
            },
        ],
    });

@Injectable({
    providedIn: 'root',
})
export class PersonalReductionsService {
    constructor(
        private personalReductioRequisitionControllerService: PersonalReductioRequisitionControllerService,
        private activesResponsibilityControllerService: ActivesResponsibilityControllerService
    ) {}

    /**
     * Obtiene todas las reducciones de personal
     */
    getAllPersonalReduction = (
        params: any = {}
    ): Observable<PersonalReductionRequisitionWithRelations[]> => {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerFind(personalReductionFilter)
            .pipe(
                tap((response: any) => {
                    if (!response.success && response.statusCode !== 200) {
                        throw new Error(
                            response.message || 'Error al obtener reducciones'
                        );
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching personal reductions:', error);
                    return throwError(() => error);
                })
            );
    };

    /**
     * Obtiene una reducción de personal por ID
     */
    getPersonalReductionById(
        id: string
    ): Observable<PersonalReductionRequisitionWithRelations> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerFindById(
                id,
                personalReductionFilter
            )
            .pipe(
                tap((response: any) => {
                    if (!response.success && response.statusCode !== 200) {
                        throw new Error(
                            response.message || 'Error al obtener la reducción'
                        );
                    }
                }),
                catchError((error) => {
                    console.error(
                        `Error fetching personal reduction with id ${id}:`,
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Obtiene una reducción de personal por ID desde el link publico
     */
    findByIdFromLink(id: string, tokenLink: string): Observable<any> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerFindByIdFromLink(
                id,
                tokenLink
            )
            .pipe(
                tap((response: any) => {
                    if (!response.success && response.statusCode !== 200) {
                        throw new Error(
                            response.message || 'Error al obtener la reducción'
                        );
                    }
                }),
                catchError((error) => {
                    console.error(
                        `Error fetching personal reduction with id ${id}:`,
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Crea una nueva reducción de personal
     */
    createPersonalReduction(
        data: Omit<PersonalReductionRequisition, 'id'>
    ): Observable<PersonalReductionRequisition> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerCreate(data)
            .pipe(
                map((response: any) => {
                    if (!response.success) {
                        throw new Error(
                            response.message || 'Error al crear la reducción'
                        );
                    }
                    return response.data;
                }),
                catchError((error) => {
                    console.error('Error creating personal reduction:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Actualiza una reducción de personal
     */
    updatePersonalReduction(
        id: string,
        data: Partial<PersonalReductionRequisition>
    ): Observable<void> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerUpdateById(id, data)
            .pipe(
                map(() => void 0),
                catchError((error) => {
                    console.error(
                        `Error updating personal reduction with id ${id}:`,
                        error
                    );
                    return throwError(() => error);
                })
            );
    }

    /**
     * Elimina una reducción de personal
     */
    deletePersonalReduction(id: string): Observable<boolean> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerDeleteById(id)
            .pipe(
                map(() => true),
                catchError((error) => {
                    console.error(
                        `Error deleting personal reduction with id ${id}:`,
                        error
                    );
                    return throwError(() => error);
                })
            );
    }
    /**
     * Obtiene las acciones disponibles para una reducción de personal
     */
    getAvailableActions(id: string | null): Observable<any> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerGetAvailableActions(id)
            .pipe(
                tap((response: any) => {
                    if (!response.success || response.statusCode !== 200) {
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching personal:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Envía una reducción de personal para aprobación
     */
    submitForApproval(id: string, data: { comments: string }): Observable<any> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerSubmitForApproval(id, data)
            .pipe(
                catchError((error) => {
                    console.error('Error submitting requisition:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Aprueba una reducción de personal
     */
    approveReduction(id: string, data: { comments: string }): Observable<any> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerApproveRequisition(id, data)
            .pipe(
                tap((response: any) => {
                    if (!response.success || response.statusCode !== 200) {
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching personal:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Rechaza una reducción de personal
     */
    rejectReduction(id: string, data: { comments: string }): Observable<any> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerRejectRequisition(id, data)
            .pipe(
                tap((response: any) => {
                    if (!response.success || response.statusCode !== 200) {
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching personal:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Obtiene los activos
     */
    getActives(): Observable<any> {
        return this.activesResponsibilityControllerService
            .activesResponsibilityControllerFind()
            .pipe(
                tap((response: any) => {
                    if (!response.success || response.statusCode !== 200) {
                    }
                }),
                catchError((error) => {
                    console.error('Error fetching personal:', error);
                    return throwError(() => error);
                })
            );
    }
    approveRequisitionPublic(
        requisitionId: string,
        token: string,

        requestBody: { comments: string }
    ): Observable<any> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerApproveRequisitionPublic(
                requisitionId,
                token,
                requestBody
            )
            .pipe(
                catchError((error) => {
                    console.error('Error approving requisition:', error);
                    return throwError(error);
                })
            );
    }

    rejectRequisitionPublic(
        requisitionId: string,
        token: string,
        requestBody: { comments: string }
    ): Observable<any> {
        return this.personalReductioRequisitionControllerService
            .personalReductioRequisitionControllerRejectRequisitionPublic(
                requisitionId,
                token,
                requestBody
            )
            .pipe(
                catchError((error) => {
                    console.error('Error rejecting requisition:', error);
                    return throwError(error);
                })
            );
    }
}
